import { type ComponentProps, type ReactNode, type RefObject } from "react";
import { ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CredentialVaultTabs } from "@/features/credentials/components/credential-vault-tabs";
import type { CredentialVaultTab } from "@/features/credentials/components/credential-vault-view";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from "@/components/ui/sheet";
import { CredentialsView } from "@/features/credentials/components/credentials-view";
import type { CredentialsResult } from "@/features/credentials/types";
import { cn } from "@/lib/utils";
import styles from "@/features/credentials/components/credential-vault-panel.module.css";

type CredentialCardActions = Pick<
  ComponentProps<typeof CredentialsView>,
  | "readOnly"
  | "onEditCredential"
  | "onOpenDocument"
  | "onOpenVerificationEvidence"
  | "isOpeningDocument"
  | "onRemoveDocument"
>;

const LOADING_PANEL_TITLE = "Loading provider credentials";

export function CredentialVaultPanel({
  userId,
  data,
  open,
  onOpenChange,
  initialSection,
  cardActions,
  children,
  openerRef,
  notice,
  filesView,
  closeDisabled = false,
  summaryActions,
  uploadArea,
  sectionContent,
  sectionBodies,
  credentialsHeader,
  title = "Credential Vault",
  description = "View and edit your credentials and supporting documents.",
  dialogTitle,
  headerIcon,
  headerActions,
  headerNotice,
  recordFallback,
  credentialsHeading,
  isHeaderLoading = false,
  skipEntryAnimation = false,
  embedded = false,
  view,
}: {
  userId: string;
  data: CredentialsResult | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSection?: string;
  cardActions: CredentialCardActions;
  children: ReactNode;
  openerRef?: RefObject<HTMLElement | null>;
  notice?: ReactNode;
  filesView?: ReactNode;
  closeDisabled?: boolean;
  summaryActions?: ReactNode;
  uploadArea?: ReactNode;
  sectionContent?: Partial<Record<string, ReactNode>>;
  sectionBodies?: Partial<Record<string, ReactNode>>;
  credentialsHeader?: ReactNode;
  title?: string;
  description?: ReactNode;
  dialogTitle?: string;
  headerIcon?: ReactNode;
  headerActions?: ReactNode;
  headerNotice?: ReactNode;
  recordFallback?: ReactNode;
  credentialsHeading?: string;
  /** Show placeholders in place of the title, subtitle and icon. */
  isHeaderLoading?: boolean;
  /** Set when this panel replaces a loading panel that is already open. */
  skipEntryAnimation?: boolean;
  /** Render the tabs inline on a page, without the sheet, header or Done. */
  embedded?: boolean;
  /** The view an embedded panel shows, chosen by the page's tabs. */
  view?: CredentialVaultTab;
}) {
  const tabs = (
    <CredentialVaultTabs
      view={embedded ? view : undefined}
      files={filesView ?? recordFallback}
      credentials={
        data ? (
          <CredentialsView
            data={data}
            userId={userId}
            layout="panel"
            heading={credentialsHeading}
            initialSection={initialSection}
            summaryActions={summaryActions}
            uploadArea={uploadArea}
            sectionContent={sectionContent}
            sectionBodies={sectionBodies}
            header={credentialsHeader}
            notice={notice}
            {...cardActions}
          />
        ) : (
          recordFallback
        )
      }
    />
  );
  if (embedded) {
    return (
      <div className="flex h-[min(75dvh,56rem)] min-h-[28rem] flex-col overflow-hidden rounded-xl border bg-background">
        {headerNotice && (
          <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-4 sm:px-6">
            {headerNotice}
          </div>
        )}
        {tabs}
        {children}
      </div>
    );
  }
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        skipEntryAnimation={skipEntryAnimation}
        className={cn(
          styles.panel,
          "flex w-full flex-col gap-0 border-slate-200 bg-white p-0 sm:max-w-2xl sm:rounded-l-2xl",
        )}
        onCloseAutoFocus={(event) => {
          if (openerRef?.current) {
            event.preventDefault();
            openerRef.current.focus();
          }
        }}
      >
        <div className="flex shrink-0 flex-wrap items-center gap-3 border-b border-slate-200 px-4 py-6 pr-16 sm:px-6 sm:pr-16">
          {isHeaderLoading ? (
            <Skeleton className="size-12 shrink-0 rounded-full" />
          ) : (
            (headerIcon ?? (
              <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-primary/5 text-primary">
                <ShieldCheck className="size-6" aria-hidden="true" />
              </div>
            ))
          )}
          <div className="min-w-0 flex-1">
            <SheetTitle
              className={cn(
                "text-2xl font-semibold tracking-tight text-slate-900",
                (dialogTitle || isHeaderLoading) && "sr-only",
              )}
            >
              {isHeaderLoading ? LOADING_PANEL_TITLE : (dialogTitle ?? title)}
            </SheetTitle>
            {isHeaderLoading && (
              <div aria-hidden="true">
                <Skeleton className="h-7 w-48" />
                <Skeleton className="mt-2 h-4 w-32" />
              </div>
            )}
            {dialogTitle && !isHeaderLoading && (
              <h2 className="break-words text-2xl font-semibold tracking-tight text-slate-900">
                {title}
              </h2>
            )}
            <SheetDescription
              className={cn(
                "mt-2 text-sm leading-6 text-slate-500",
                isHeaderLoading && "sr-only",
              )}
            >
              {isHeaderLoading ? LOADING_PANEL_TITLE : description}
            </SheetDescription>
          </div>
          {headerActions && (
            <div className="w-full shrink-0 empty:hidden sm:w-auto">
              {headerActions}
            </div>
          )}
        </div>
        {headerNotice && (
          <div className="shrink-0 border-b border-slate-200 bg-white px-4 py-4 sm:px-6">
            {headerNotice}
          </div>
        )}
        {tabs}
        <div className="shrink-0 border-t border-slate-200 bg-white px-4 py-3 sm:px-6">
          <Button
            variant="outline"
            disabled={closeDisabled}
            onClick={() => onOpenChange(false)}
            className="h-10 w-full gap-2 border-slate-300 font-semibold"
          >
            Done
          </Button>
        </div>
        {/* Keep editor and preview dialogs in the sheet's focus hierarchy so
            Escape dismisses the topmost dialog and returns focus to the panel. */}
        {children}
      </SheetContent>
    </Sheet>
  );
}
